import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {toggle} from 'opticks'

import {Button} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {isApple} from '@vio/core/src/_web/utils/userAgent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {getOnAuthSuccessLandingUrl} from '@vio/core/src/auth/business/persistData'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {
  getAuthenticationFlow,
  getSusiAnalyticsData
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  AuthenticationMethod
} from '@vio/core/src/auth/types/Auth'
import {AuthenticationProvider} from '@vio/core/src/auth/types/Cognito'
import {isEmailOtpSignupEnabled} from '@vio/core/src/auth/utils/otp'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {isReactNativeWebView} from '@vio/core/src/native'

import {HandleLogin} from '../types'

const ButtonsWrapper = styled.div`
  > button + button {
    margin-top: ${cssTheme.layout.spacing.s400};
  }
`

interface Props {
  handleLogin: HandleLogin
  handleEmailLogin?: (e: React.SyntheticEvent) => void
  handleOnClose?: () => void
}

export const SignInButtons = ({
  handleLogin,
  handleEmailLogin,
  handleOnClose
}: Props) => {
  const dispatch = useDispatch()
  const analyticsData = useSelector(getSusiAnalyticsData)
  const isAppleDevice = isApple()
  const successLandingUrl = getOnAuthSuccessLandingUrl()
  const authenticationFlow = useSelector(getAuthenticationFlow)
  const isPreCheckout = authenticationFlow === AuthenticationFlows.PreCheckout
  const isWebView = isReactNativeWebView()
  const {brandCode} = useBrand()
  const isEmailOtpEnabled = isEmailOtpSignupEnabled(isWebView, brandCode)

  // added as part of 567c4139-signin-to-complete-booking
  const handleContinueAsGuest = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SkipAuthentication,
        action: Action.Clicked,
        team: Team.Retention,
        payload: {
          authenticationFlow: AuthenticationFlows.PreCheckout
        }
      })
    )

    // If we don't have a success landing URL, simply close the sheet
    if (!successLandingUrl) {
      handleOnClose?.()
      return
    }

    // Otherwise redirect after giving analytics a chance to track the event
    setTimeout(() => {
      window.location.href = successLandingUrl
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }, [dispatch, successLandingUrl, handleOnClose])

  return (
    <ButtonsWrapper>
      {isAppleDevice && (
        <Button
          fullWidth
          dataId="SocialLoginApple"
          size="xl"
          iconStart={<Icon name="Apple" />}
          variant="secondary"
          onClick={handleLogin(
            AuthenticationMethod.Apple,
            AuthenticationProvider.Apple,
            analyticsData
          )}
        >
          <FormattedMessageWrapper
            id="SUSI.continueWith"
            defaultMessage="Continue with {channel}"
            values={{channel: 'Apple'}}
          />
        </Button>
      )}

      <Button
        fullWidth
        variant="secondary"
        dataId="SocialLoginGoogle"
        size="xl"
        iconStart={<Icon name="GoogleColor" />}
        onClick={handleLogin(
          AuthenticationMethod.Google,
          AuthenticationProvider.Google,
          analyticsData
        )}
      >
        <FormattedMessageWrapper
          id="SUSI.continueWith"
          defaultMessage="Continue with {channel}"
          values={{channel: 'Google'}}
        />
      </Button>

      <Button
        fullWidth
        variant="secondary"
        dataId="SocialLoginFacebook"
        size="xl"
        iconStart={<Icon name="FacebookColor" />}
        onClick={handleLogin(
          AuthenticationMethod.Facebook,
          AuthenticationProvider.Facebook,
          analyticsData
        )}
      >
        <FormattedMessageWrapper
          id="SUSI.continueWith"
          defaultMessage="Continue with {channel}"
          values={{channel: 'Facebook'}}
        />
      </Button>

      {isEmailOtpEnabled && (
        <Button
          fullWidth
          variant="secondary"
          dataId="SocialLoginEmail"
          size="xl"
          iconStart={<Icon name="Mail" />}
          onClick={handleEmailLogin}
        >
          <FormattedMessageWrapper
            id="SUSI.continueWith"
            defaultMessage="Continue with {channel}"
            values={{channel: 'Email'}}
          />
        </Button>
      )}

      {isPreCheckout &&
        toggle(
          '567c4139-signin-to-complete-booking',
          false,
          <Button
            fullWidth
            variant="quiet"
            dataId="ContinueAsGuest"
            size="xl"
            onClick={handleContinueAsGuest}
          >
            <FormattedMessageWrapper
              id="SUSI.continueAsGuest"
              defaultMessage="Continue as guest"
            />
          </Button>
        )}
    </ButtonsWrapper>
  )
}
